import React, { useEffect, useMemo, useState } from "react";
import "./AdminRequestedLeave.css";

const toArray = (x) => (Array.isArray(x) ? x : x?.data && Array.isArray(x.data) ? x.data : []);
const normType = (t) => {
  const s = String(t || "").trim().toLowerCase();
  if (s.includes("half")) return "Half-day";
  if (s === "casual") return "Casual";
  if (s === "sick") return "Sick";
  if (s === "lop" || s === "loss of pay") return "LOP";
  if (s === "paid" || s === "earned" || s === "pl") return "Paid";
  return t || "-";
};
const pad2 = (n) => String(n).padStart(2, "0");

const MONTH_OPTIONS = [
  { value: "", label: "--All Months--" },
  { value: 0, label: "Jan" },
  { value: 1, label: "Feb" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Apr" },
  { value: 4, label: "May" },
  { value: 5, label: "Jun" },
  { value: 6, label: "Jul" },
  { value: 7, label: "Aug" },
  { value: 8, label: "Sep" },
  { value: 9, label: "Oct" },
  { value: 10, label: "Nov" },
  { value: 11, label: "Dec" }
];

function daysBetween(start, end, type) {
  // Calculate total days inclusive (if half-day, just 0.5)
  const sD = new Date(start);
  const eD = new Date(end);
  if (isNaN(sD) || isNaN(eD)) return 0;
  if (normType(type) === "Half-day") return 0.5;
  // Day difference, including both start and end dates
  return Math.floor((eD - sD) / (1000 * 60 * 60 * 24)) + 1;
}

function convertToCSV(data) {
  if (!data.length) return "";
  const fields = Object.keys(data[0]);
  const rows = [fields.join(",")];
  data.forEach((row) =>
    rows.push(fields.map((f) => `"${row[f] ?? ""}"`).join(","))
  );
  return rows.join("\n");
}
function downloadCSV(data, fileName) {
  const csv = convertToCSV(data);
  const blob = new Blob([csv], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
}

function AdminRequestedLeave() {
  const [leaveList, setLeaveList] = useState([]);
  const [filteredLeaveList, setFilteredLeaveList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(today.getMonth()));
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [updatingIdx, setUpdatingIdx] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const currentMonthKey = useMemo(() => {
    if (selectedMonth === "") return `${selectedYear}-all`;
    const m = pad2(Number(selectedMonth) + 1);
    return `${selectedYear}-${m}`;
  }, [selectedMonth, selectedYear]);

  function fetchLeaves() {
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=all_leave")
      .then((res) => res.json())
      .then((data) => {
        const arr = toArray(data);
        setLeaveList(arr);
        setEmployeeList([...new Set(arr.map((leave) => leave.employee).filter(Boolean))]);
        setFilteredLeaveList(arr);
      })
      .catch(() => {
        setLeaveList([]);
        setFilteredLeaveList([]);
      });
  }

  useEffect(() => {
    fetchLeaves();
  }, []);

  useEffect(() => {
    if (!selectedEmployee) setFilteredLeaveList(leaveList);
    else setFilteredLeaveList(leaveList.filter((l) => l.employee === selectedEmployee));
  }, [leaveList, selectedEmployee]);

  function handleStatusEdit(idx, leave) {
    setUpdatingIdx(idx);
    setNewStatus(leave.status);
  }
  function handleStatusSave(idx, leave) {
    setSaving(true);
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=update_leave_status", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        startDate: leave.startDate,
        endDate: leave.endDate,
        employee: leave.employee,
        leaveType: leave.leaveType,
        reason: leave.reason,
        status: newStatus,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          fetchLeaves();
          setUpdatingIdx(null);
        }
        setSaving(false);
      })
      .catch(() => setSaving(false));
  }

  function handleDelete(idx, leave) {
    if (!window.confirm("Are you sure you want to delete this leave request?")) return;
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=delete_leave", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uniqueId: leave.uniqueId
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          fetchLeaves();
        }
      })
      .catch(() => { }); // Silently fail
  }

  const tableRows = useMemo(() => {
    const base = toArray(filteredLeaveList);
    return base.filter((r) => {
      const d = new Date(r.startDate);
      if (isNaN(d)) return false;
      const matchYear = d.getFullYear() === Number(selectedYear);
      const matchMonth = selectedMonth === "" || d.getMonth() === Number(selectedMonth);
      return matchYear && matchMonth;
    });
  }, [filteredLeaveList, selectedMonth, selectedYear]);

  // Totals calculation by day-count (approved only)
  const sidebarAllEmployees = useMemo(() => {
    if (selectedEmployee) return [];
    const acc = {};
    for (const r of toArray(leaveList)) {
      if (String(r.status).toLowerCase() !== "approved") continue;
      const d = new Date(r.startDate);
      if (isNaN(d)) continue;
      const passYear = d.getFullYear() === Number(selectedYear);
      const passMonth = selectedMonth === "" || d.getMonth() === Number(selectedMonth);
      if (!passYear || !passMonth) continue;
      const name = r.employee || "-";
      const t = normType(r.leaveType);
      const cnt = daysBetween(r.startDate, r.endDate, r.leaveType);
      acc[name] = (acc[name] || 0) + cnt;
    }
    return Object.entries(acc)
      .map(([employee, total]) => ({ employee, total: Number(total) }))
      .sort((a, b) => b.total - a.total);
  }, [leaveList, selectedEmployee, selectedMonth, selectedYear]);

  // Individual sidebar info, grouped by leave type (day-count)
  const sidebarInfo = useMemo(() => {
    if (!selectedEmployee) return null;
    const leaves = leaveList.filter(
      (l) =>
        l.employee === selectedEmployee &&
        (selectedMonth === "" || new Date(l.startDate).getMonth() === Number(selectedMonth)) &&
        new Date(l.startDate).getFullYear() === Number(selectedYear) &&
        String(l.status).toLowerCase() === "approved"
    );
    const acc = { Casual: 0, Sick: 0, "Half-day": 0, LOP: 0, Paid: 0, Other: 0 };
    let totalTaken = 0;
    for (const r of leaves) {
      const t = normType(r.leaveType);
      const cnt = daysBetween(r.startDate, r.endDate, r.leaveType);
      if (Object.prototype.hasOwnProperty.call(acc, t)) acc[t] += cnt;
      else acc.Other += cnt;
      totalTaken += cnt;
    }
    return {
      byType: acc,
      totalTaken,
      total: totalTaken,
    };
  }, [selectedEmployee, leaveList, selectedMonth, selectedYear]);

  return (
    <div className="leave-main-container container-fluid">
      {/* Left: table and filters */}
      <div className="leave-table-section col-12 col-lg-8">
        <h2 className="leave-title">Requested Leaves</h2>
        <div className="leave-filter-row">
          <div className="row gx-2">
            <div className="col-12 col-sm-auto">
              <label>
                <b>Filter by Employee:</b>
                <select
                  value={selectedEmployee}
                  onChange={e => setSelectedEmployee(e.target.value)}
                  className="leave-filter-select ms-2"
                >
                  <option value="">--All Employees--</option>
                  {employeeList.map((emp) => (
                    <option key={emp} value={emp}>{emp}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="col-12 col-sm-auto">
              <label>
                <b>Month:</b>
                <select
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  className="leave-filter-select ms-2"
                >
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m.label} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="col-12 col-sm-auto">
              <label>
                <b>Year:</b>
                <input
                  type="number"
                  className="leave-filter-select ms-2"
                  value={selectedYear}
                  onChange={e => setSelectedYear(Number(e.target.value))}
                  style={{ width: 80 }}
                />
              </label>
            </div>
            <div className="col-12 col-sm-auto">
              {selectedEmployee && (
                <button
                  className="leave-btn-download"
                  onClick={() => downloadCSV(tableRows, `leave_records_${selectedEmployee}_${currentMonthKey}.csv`)}
                >
                  Download CSV
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="leave-table-container">
          <table className="leave-table">
            <thead>
              <tr>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Employee</th>
                <th>Leave Type</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(tableRows) ? tableRows : []).map((leave, idx) => (
                <tr key={`${leave.employee}-${leave.startDate}-${leave.endDate}-${idx}`}>
                  <td>{leave.startDate}</td>
                  <td>{leave.endDate}</td>
                  <td>{leave.employee}</td>
                  <td>{leave.leaveType}</td>
                  <td>{leave.reason}</td>
                  <td>
                    {updatingIdx === idx ? (
                      <select
                        className="leave-status-select"
                        value={newStatus}
                        onChange={e => setNewStatus(e.target.value)}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Reject">Reject</option>
                      </select>
                    ) : (
                      leave.status
                    )}
                  </td>
                  <td>
                    {updatingIdx === idx ? (
                      <button
                        className="leave-btn-save"
                        disabled={saving}
                        onClick={() => handleStatusSave(idx, leave)}
                      >
                        Save
                      </button>
                    ) : (
                      <>
                        <button
                          className="leave-btn-edit"
                          onClick={() => handleStatusEdit(idx, leave)}
                        >
                          Edit
                        </button>
                        <button
                          className="leave-btn-delete"
                          style={{ marginLeft: 8, background: "#dc3545", color: "#fff", border: "none", padding: "4px 10px", borderRadius: 4, cursor: "pointer" }}
                          onClick={() => handleDelete(idx, leave)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(!tableRows || tableRows.length === 0) && (
                <tr><td colSpan="7">No leave records for selected month/year</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Sidebar */}
      <div className={selectedEmployee ? "leave-sidebar-indiv col-12 col-lg-4" : "leave-sidebar-all col-12 col-lg-4"}>
        {!selectedEmployee ? (
          <>
            <h3 className="leave-sidebar-title">Totals (Days)</h3>
            <table className="leave-sidebar-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Approved Days</th>
                </tr>
              </thead>
              <tbody>
                {sidebarAllEmployees.length > 0 ? (
                  sidebarAllEmployees.map((row) => (
                    <tr key={row.employee}>
                      <td>{row.employee}</td>
                      <td>{row.total}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="2">No approved leaves for selection</td></tr>
                )}
              </tbody>
            </table>
          </>
        ) : (
          <>
            <h3 className="leave-sidebar-title">{selectedEmployee}'s Leave Summary</h3>
            <div style={{ marginBottom: 12 }}>
              <b>Total Leave Taken:</b> <span>{sidebarInfo?.totalTaken ?? 0}</span>
            </div>
            <table className="leave-sidebar-table">
              <thead>
                <tr>
                  <th>Leave Type</th>
                  <th>Total Days</th>
                </tr>
              </thead>
              <tbody>
                {sidebarInfo &&
                  ["Casual", "Sick", "Half-day", "LOP", "Paid", "Other"].map((type) => (
                    <tr key={type}>
                      <td>{type}</td>
                      <td>{sidebarInfo.byType[type] ?? 0}</td>
                    </tr>
                  ))}
                <tr>
                  <td><b>Total</b></td>
                  <td><b>{sidebarInfo?.total ?? 0}</b></td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminRequestedLeave;
